<template>
  <transition
    name="accordion"
    v-on:before-enter="beforeEnter"
    v-on:enter="enter"
    v-on:before-leave="beforeLeave"
    v-on:leave="leave"
  >
    <div id="filter-panel" class="filter-panel" v-if="!isFetching" v-show="isOpen">
      <v-container>
        <v-row justify="center">

          <v-col cols="10" lg="8" align="center">
            <AutocompleteInput
              :pois="allVenues"
            />
          </v-col>

          <v-col cols="10" lg="8">
            <v-row justify="space-between">
              <v-col
                v-for="(checkbox, index) in checkboxes"
                :key="index"
                cols="auto"
              >
                <CheckboxesGroup
                  :ref="checkbox.name"
                  :label="checkbox.label"
                  :name="checkbox.name"
                  :checkboxes="checkbox.checkboxes"
                  @change="onChange"
                />
              </v-col>
            </v-row>
          </v-col>

        </v-row>

        <v-row justify="center">

          <v-col cols="10" lg="8" align="center">
            <PeopleSlider
              :ref="slider.name"
              :label="slider.label"
              :name="slider.name"
              @change="onChange"
            />
          </v-col>

          <v-col cols="10" lg="8">
            <SearchValidateButton
              @validate="applyFilter"
              :is-calculating="loading.isCalculatingResults"
              :number-of-results="numberOfResults"
            />
          </v-col>

        </v-row>
      </v-container>
    </div>
  </transition>
</template>

<script>
  import { serialize } from '../../utils'

  import AutocompleteInput from './AutocompleteInput.vue'
  import CheckboxesGroup from './CheckboxesGroup'
  import PeopleSlider from './PeopleSlider'
  import SearchValidateButton from './SearchValidateButton'

  export default {
    name: 'VenuesFilterPanel',

    props: {
      isFetching: {
        required: true,
        type: Boolean,
      },

      isOpen: {
        default: false,
        type: Boolean,
      },

      allPois: {
        required: true,
        type: Array,
      },
    },

    components: {
      SearchValidateButton,
      PeopleSlider,
      CheckboxesGroup,
      AutocompleteInput,
    },

    data() {
      return {
        loading: {
          isCalculatingResults: false,
        },
        filters: {},
        filteredVenues: [],
      }
    },

    computed: {
      locale() {
        return this.$store.state.locale
      },

      allVenues() {
        return this.allPois.filter((poi) => poi.venue_finder_category === 'Venue')
      },

      numberOfResults() {
        return this.filteredVenues.length
      },

      checkboxes() {
        return [
          {
            label: this.$t('search.categories.title'),
            name: 'themes',
            checkboxes: [
              {
                label: this.$tc('categories.hotel', 1),
                name: 'hotel',
              },
              {
                label: this.$tc('categories.congress', 1),
                name: 'congress',
              },
              {
                label: this.$tc('categories.event'),
                name: 'event',
              },
              {
                label: this.$tc('categories.restaurant'),
                name: 'restaurant',
              },
            ],
          },
          {
            label: this.$t('search.filter.seats.title'),
            name: 'seats',
            checkboxes: [
              {
                label: this.$t('search.filter.seats.banquets'),
                name: 'banquet',
              },
              {
                label: this.$t('search.filter.seats.concerts'),
                name: 'concert',
              },
              {
                label: this.$t('search.filter.seats.seminars'),
                name: 'seminar',
              },
            ],
          },
        ]
      },

      slider() {
        return {
          label: this.$t('search.filter.people-number'),
          name: 'peopleNumber',
        }
      },
    },

    methods: {
      beforeEnter: function (el) {
        el.style.height = '0'
      },

      enter: function (el) {
        el.style.height = el.scrollHeight + 'px'
      },

      beforeLeave: function (el) {
        el.style.height = el.scrollHeight + 'px'
      },

      leave: function (el) {
        el.style.height = '0'
      },

      onChange(e) {
        if (e) {
          const { name, values } = e

          this.filters[name] = values
        }

        this.$nextTick(() => this.filterVenues())
      },

      filterVenues() {
        const { seats, themes, peopleNumber } = this.filters

        this.loading.isCalculatingResults = true

        const trueThemes = Object.keys(themes).filter((theme) => themes[theme])
        const filteredByThemes = this.allVenues.filter((venue) => trueThemes.includes(venue.venue_finder_sub_category))

        const [minPeople, maxPeople] = peopleNumber
        const trueSeats = Object.keys(seats).filter((seat) => seats[seat])

        const filteredBySeats = filteredByThemes.filter((venue) => {
          const { facilitiesAverage } = venue

          if (!facilitiesAverage) return false

          let atLeastOneOrderMatch = false

          const allMatchingMin = []
          const allMatchingMax = []
          for (let i = 0; i < facilitiesAverage.length; i += 1) {
            const orderOfMagnitude = facilitiesAverage[i]

            if (minPeople >= orderOfMagnitude.min) allMatchingMin.push(i)
            if (maxPeople <= orderOfMagnitude.max) allMatchingMax.push(i)
          }

          const lastMatchingMin = allMatchingMin[allMatchingMin.length - 1]
          const firstMatchingMax = allMatchingMax[0]

          const relevantOrderOfMagnitude = []
          for (let i = lastMatchingMin; i <= firstMatchingMax; i += 1) {
            relevantOrderOfMagnitude.push(facilitiesAverage[i])
          }

          for (const orderOfMagnitude of relevantOrderOfMagnitude) {
            for (const seat of trueSeats) {
              if (orderOfMagnitude[seat]) atLeastOneOrderMatch = true
            }
          }

          return atLeastOneOrderMatch
        })

        this.filteredVenues = filteredBySeats

        // Timeout before removing the loading sign because it's too fast otherwise and the result number is jumping
        // Random beetween 200 and 600 ms to avoid the same time at every change
        const min = 200
        const max = 500
        const interval = Math.floor(Math.random() * (max - min + 1) + min)
        setTimeout(() => this.loading.isCalculatingResults = false, interval)
      },

      applyFilter() {
        this.$emit('filter', this.filteredVenues)

        const serializedFilter = serialize(this.filters)

        history.pushState({}, null, `/${this.locale}?${serializedFilter}&query=true`)
      },
    },
  }
</script>

<style lang="sass" scoped>
  @import "@/assets/styles/_config.sass"

  #filter-panel
    background-color: $primary-color
    transition: 150ms ease-out
    overflow: hidden
    color: #fff !important

  .slide-fade-enter-active
    transition: all .3s ease

  .slide-fade-leave-active
    transition: all .8s cubic-bezier(1.0, 0.5, 0.8, 1.0)

  .slide-fade-enter, .slide-fade-leave-to
    transform: translateX(10px)
    opacity: 0

</style>
